<template>
  <v-card
    v-if="selected"
  >
    <v-card-title class="headline">
      {{ product.name }} <span class="pl-4">({{ product.real_price | money }})</span>
      <v-spacer/>
      <v-btn
        icon
        @click="cancel">
        <v-icon large>close</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider/>

    <v-card-text class="pa-0">
      <v-layout class="pa-3">
        <v-flex
          lg8
          md12
          class="pr-3"
        >
          <v-layout>
            <v-flex
              xs12
              lg3
            >
              <v-layout wrap>
                <v-flex xs12>
                  <v-avatar
                    size="160"
                    tile
                  >
                    <v-img
                      :src="$helpers.imageFrom(product, 'small')"
                      height="160"
                      cover
                    />
                  </v-avatar>
                </v-flex>
                <v-flex xs12>
                  <order-draft-item-availability
                    :product="product"
                    class="ma-2"
                  />
                </v-flex>
              </v-layout>
            </v-flex>

            <v-flex
              v-if="!isValid"
              lg9
              xs12
            >
              <v-alert
                :value="true"
                type="error"
              >
                <p class="font-weight-regular">
                  {{ $t('Product bundle looks damaged, probably some product has been removed.') }}
                </p>
                <p class="font-weight-bold mb-0">
                  {{ $t('Please contact the store service.') }}
                </p>
              </v-alert>
            </v-flex>

            <v-flex
              v-else
              lg9
              xs12
            >
              <loading-state-micro :loading="!showTabs" />
              <v-tabs
                v-if="showTabs"
                v-model="tabs"
                grow
              >
                <v-tab
                  v-if="showProperties || showBundleOptions"
                  :key="0"
                >
                  <v-badge color="red">
                    <template v-slot:badge>
                      <span v-if="!isConfigured">!</span>
                    </template>
                    <span>{{ $t('Options') }}</span>
                  </v-badge>
                </v-tab>

                <v-tab
                  v-if="showExtras"
                  :key="1"
                >
                  {{ $t('Extras') }}
                </v-tab>

                <v-tab :key="2">
                  {{ $t('Notes') }}
                </v-tab>

                <v-tab-item
                  v-if="showProperties"
                  :key="0"
                >
                  <order-draft-item-variants
                    :disabled="!selected"
                    :product="product"
                    @selected="onSimpleVariantSelected"
                  />
                </v-tab-item>

                <v-tab-item
                  v-if="showBundleOptions"
                  :key="0"
                >
                  <order-draft-item-bundle-selection
                    :components="components"
                    @selected="onComponentsSelected"
                  />
                </v-tab-item>

                <v-tab-item
                  v-if="showExtras"
                  :key="1"
                >
                  <order-draft-item-extras
                    :disabled="!selected"
                    :product="product"
                    @selected="onExtrasSelected"
                  />
                </v-tab-item>

                <v-tab-item :key="2">
                  <order-draft-item-notes
                    :product="product"
                    :order="order"
                    no-add-button
                    @note-added="onNoteAdded"
                  />
                </v-tab-item>
              </v-tabs>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex
          v-if="isValid"
          md12
          lg4
          class="px-4"
          right
        >
          <order-draft-item-properties
            :selected="selected"
            class="pl-1"
          />
          <order-draft-item-quantity
            v-model="quantity"
            :disabled="!selected"
            :min="parseInt(minQuantity)"
            :max="parseInt(maxQuantity)"
          />
          <item-tiers
            v-if="selected.tiers && selected.tiers.length"
            v-model="quantity"
            :tier-prices="selected.tiers || []"
            :customer-groups="order.customer.groups || []"
            large
          />
        </v-flex>
      </v-layout>
    </v-card-text>

    <v-divider/>

    <v-card-actions class="ma-3">
      <v-spacer/>
      <v-btn
        :disabled="product == null || !isConfigured || !isValid"
        color="primary"
        small
        @click="confirm"
      >
        {{ $t('Add Product') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import OrderDraftItemQuantity from '@/modules/Orders/components/OrderDraft/OrderDraftItemQuantity'
import OrderDraftItemAvailability from '@/modules/Orders/components/OrderDraft/OrderDraftItemAvailability'
import OrderDraftItemVariants from '@/modules/Orders/components/OrderDraft/OrderDraftItemVariants'
import OrderDraftItemNotes from '@/modules/Orders/components/OrderDraft/OrderDraftItemNotes'
import OrderDraftItemExtras from '@/modules/Orders/components/OrderDraft/OrderDraftItemExtras'
import OrderDraftItemBundleSelection from '@/modules/Orders/components/OrderDraft/OrderDraftItemBundleSelection'
import OrderDraftItemProperties from '@/modules/Orders/components/OrderDraft/OrderDraftItemProperties'
import LoadingStateMicro from '@/components/LoadingStateMicro'
import ItemTiers from '@/modules/Orders/components/Items/ItemTiers'

export default {
  name: 'OrderDraftProductDialog',
  components: {
    ItemTiers,
    LoadingStateMicro,
    OrderDraftItemProperties,
    OrderDraftItemBundleSelection,
    OrderDraftItemExtras,
    OrderDraftItemNotes,
    OrderDraftItemVariants,
    OrderDraftItemAvailability,
    OrderDraftItemQuantity
  },
  props: {
    product: {
      validator: prop => typeof prop === 'object' || prop === null,
      default: null
    },

    order: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      tabs: 0,
      dialog: false,
      selected: null,
      quantity: 1,
      components: [],
      isValid: true
    }
  },

  computed: {
    minQuantity () {
      if (this.isSelected) {
        return this.selected.inventory.min_per_order || 1
      }

      return this.product.inventory.min_per_order || 1
    },

    maxQuantity () {
      if (this.isSelected) {
        return this.selected.inventory.max_per_order || null
      }

      return this.product.inventory.max_per_order || null
    },

    isConfigured: {
      cache: false,
      get: function () {
        if (this.product.classification === 'bundle') {
          const selected = this.selected.components.filter(c => c.selected).length
          const toSelect = this.components.filter(c => c.variants && c.variants.length).length

          return selected === toSelect
        }
        if (this.product.classification === 'common') {
          return !this.selected.variants || !this.selected.variants.length
        }
      }
    },

    showTabs () {
      if (this.product.classification === 'common') {
        return true
      }

      return this.product.classification === 'bundle' && this.components && this.components.length
    },

    showExtras () {
      return this.product.extras && this.product.extras.length
    },

    showProperties () {
      return this.product.classification === 'common' && this.product.variants && this.product.variants.length
    },

    showBundleOptions () {
      return this.product.classification === 'bundle' && this.components && this.components.length
    }
  },

  watch: {
    product () {
      if (!this.product) {
        return
      }

      this.selected = { ...this.product }
      this.selected.extras = []
      if (this.product.classification === 'bundle') {
        this.getComponents()
      }
    }
  },

  methods: {
    async getComponents () {
      const res = this.product.associated_with_bundles.length
        ? await this.$http.make('product')
          .where('id', 'like', this.product.associated_with_bundles.join(','))
          .include(['children'])
          .getFullData()
        : { collection: [] }

      if (res.collection.length !== this.product.components.length) {
        this.isValid = false
        return
      }

      this.components = []
      this.product.components.forEach(component => {
        const found = res.collection.find(el => el.id === component.id)
        const fullProduct = { ...found }
        fullProduct.key = component.key
        fullProduct.quantity = component.quantity
        this.components.push(fullProduct)

        if (!fullProduct.variants || !fullProduct.variants.length) {
          this.selected.components = this.selected.components.map(comp => {
            if (comp.id !== component.id) {
              return comp
            }

            return {
              name: fullProduct.name,
              id: comp.id,
              key: comp.key,
              quantity: component.quantity
            }
          })
        }
      })

      for (let i = 0; i < this.components.length; i++) {
        if (this.components[i].variants && this.components[i].variants.length) {
          this.componentPanel = i
          break
        }
      }
    },

    onSimpleVariantSelected (item) {
      let selected = { ...item }
      selected.product_id = item.id
      selected.notes = selected.notes && selected.notes.length > 0 ? selected.notes : []

      this.selected = selected
      this.quantity = 1
    },

    onComponentsSelected (components) {
      components = components.filter(el => !!el.selectedVariant)
      this.selected.components = this.selected.components.map(component => {
        const configured = components.find(c => c.key === component.key)
        if (!configured) {
          return component
        }

        return {
          name: configured.selectedVariant.name,
          parent_id: configured.id,
          id: configured.selectedVariant.id,
          key: component.key,
          quantity: component.quantity,
          selected: true
        }
      })
    },

    onExtrasSelected (extras) {
      if (!extras) {
        this.selected.extras = []
        return
      }

      let selectedExtras = []
      for (const key in extras) {
        if (!extras.hasOwnProperty(key)) {
          continue
        }

        const prodExtra = this.product.extras.find(e => e.key === key)
        if (!prodExtra) {
          continue
        }

        let selectedOptions = []
        extras[key].forEach(opt => {
          const selectedOpt = prodExtra.options.find(o => o.key === opt)
          if (selectedOpt) {
            selectedOptions.push(selectedOpt)
          }
        })

        selectedExtras.push({
          key: prodExtra.key,
          name: prodExtra.name,
          options: selectedOptions
        })
      }

      this.selected.extras = selectedExtras
    },

    onNoteAdded (notes) {
      this.selected.notes = notes
    },

    cancel () {
      this.selected = null
      this.components = []
      this.componentPanel = 0
      this.quantity = 1
      this.$emit('cancelled')
    },

    confirm () {
      if (!this.selected) {
        return
      }

      delete this.selected.id
      this.selected.quantity = this.quantity

      this.$emit('confirmed', this.selected)

      this.quantity = 1
      this.selected = null
    },

    removeNote (byName) {
      if (byName === 'Notes') {
        this.selected.notes = []
        return
      }

      let notes = { ...this.selected.notes }
      delete notes[byName]
      this.selected.notes = notes
      if (!this.order.hasNotes(this.item)) {
        this.selected.notes = []
      }
    }
  }
}
</script>
